import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import { useAIAvatarSession } from '../context/AIAvatarSessionContext';
import { useUserData } from './useUserData';
import { usePodData } from './usePodData';
import { regenerateQRCode } from '../apis/qr';
import { getSavedPodIP } from '../utils/helpers';

export function useEndSession() {
    const navigate = useNavigate();
    const { endSessionAndLogout } = useAuth();
    const { resetState } = useAppContext();
    const { stopSession } = useAIAvatarSession();
    const { userData } = useUserData();
    const { podId, podIP } = usePodData();
    const [isEnding, setIsEnding] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const sessionId = userData?.sessionId;

    const requestEndSession = () => {
        setShowConfirm(true);
    };

    const confirmEndSession = async () => {
        if (isEnding) return;
        setIsEnding(true);
        const isGuest = userData?.isGuest;
        const sessionStoredPodIp = getSavedPodIP();
        let reloading = false;
        try {
            await stopSession();
            if (podId) {
                try {
                    await regenerateQRCode(podId);
                } catch (error) {
                    console.error('Failed to regenerate QR code:', error);
                }
            }
            await endSessionAndLogout(sessionId, sessionStoredPodIp);
            resetState();

            if (isGuest) {
                // Guests skip the OIDC signout redirect, so nothing tears the SPA
                // down for them — reload to hand the next user a clean pod.
                reloading = true;
                window.location.replace(sessionStoredPodIp ? `/${sessionStoredPodIp}` : '/');
                return;
            }

            navigate('/');
        } catch (error) {
            console.error('End session failed:', error);
        } finally {
            if (!reloading) {
                setIsEnding(false);
                setShowConfirm(false);
            }
        }
    };

    const cancelEndSession = () => {
        setShowConfirm(false);
    };

    return {
        showConfirm,
        isEnding,
        requestEndSession,
        confirmEndSession,
        cancelEndSession,
    };
}
